'use client';

import React, { useEffect, useState } from 'react';
import { StyledText } from './StyledText';
import {
  StyledTextProps,
  StrapiStyledText,
  TextSegment,
  RichTextNode,
} from '@/types/styledText';

interface StyledTextWrapperProps {
  strapiData: StrapiStyledText;
  bgColor?: string;
}

export const StyledTextWrapper: React.FC<StyledTextWrapperProps> = ({
  strapiData,
  bgColor,
}) => {
  const [data, setData] = useState<StyledTextProps | null>(null);

  useEffect(() => {
    if (!strapiData) return;

    const processTextSegments = (segments: TextSegment[]) => {
      return segments
        .map((segment) => {
          const style = segment.style || 'solid';
          const text = segment.text || '';

          if (segment.startColor && segment.middleColor && segment.endColor) {
            return `<span style="background: linear-gradient(to right, ${segment.startColor}, ${segment.middleColor}, ${segment.endColor}); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">${text}</span>`;
          }

          if (segment.textColor) {
            return `<span style="color: ${segment.textColor}">${text}</span>`;
          }

          return `<span>${text}</span>`;
        })
        .join(' ');
    };

    const processRichText = (content: any) => {
      if (!content) return '';

      if (Array.isArray(content)) {
        return content
          .map((block) => {
            if (block.type === 'paragraph') {
              return block.children
                .map((child: any) => {
                  if (child.type === 'text') {
                    return child.text;
                  }
                  return '';
                })
                .join('');
            }
            return '';
          })
          .join('\n');
      }

      if (content.children) {
        return content.children.map((node: any) => node.text || '').join(' ');
      }

      return '';
    };

    const processedContent = strapiData.textSegments?.length
      ? processTextSegments(strapiData.textSegments)
      : processRichText(strapiData.content);

    const transformedData: StyledTextProps = {
      content: processedContent,
      textStyle: (strapiData.textStyle || 'solid') as 'solid' | 'gradient',
      textColor: strapiData.textColor || 'black',
      startColor: strapiData.startColor,
      middleColor: strapiData.middleColor,
      endColor: strapiData.endColor,
      alignment: (strapiData.alignment || 'left') as
        | 'left'
        | 'center'
        | 'right',
      size: (strapiData.size?.[0]?.size || 'H1') as
        | 'H1'
        | 'H2'
        | 'H3'
        | 'H4'
        | 'H5'
        | '40PX',
      theme: (strapiData.theme || 'default') as 'default' | 'purple' | 'black',
      background: (strapiData.background || 'inherit') as
        | 'inherit'
        | 'solid'
        | 'gradient'
        | 'gradient-mesh'
        | 'image',
      backgroundColor: strapiData.backgroundColor,
      backgroundImage: strapiData.backgroundImage?.data?.attributes?.url,
    };

    setData(transformedData);
  }, [strapiData]);

  if (!data) return null;

  return <StyledText key={`styled-text-${strapiData.id}`} {...data} />;
};
